import {
  INTERVIEW_KINDS,
  isFeedbackPosted,
  type InterviewFeedbackGroup,
  type InterviewKind,
} from 'constants/interview';
import type { Uuid } from 'store/reducers/types';
import { FEEDBACK_TITLES } from './constants';
import type { FeedbackData, FeedbackFormValues } from './types';

const MIN_AI_COMMENT_LENGTH = 10;
const ALPHANUMERIC_PATTERN = '[\\p{L}\\p{N}]';
const ALPHANUMERIC_CHARACTER = new RegExp(ALPHANUMERIC_PATTERN, 'u');

export const formValuesMapper = (values: FeedbackFormValues) => ({
  ...values,
  level: values.level?.value,
  english_level: values.english_level?.value,
});

export const canImproveCommentWithAi = (comment?: string) => {
  if (!comment) return false;

  const commentDocument = new DOMParser().parseFromString(comment, 'text/html');
  const commentText = commentDocument.body.textContent?.trim() || '';

  return commentText.length >= MIN_AI_COMMENT_LENGTH && ALPHANUMERIC_CHARACTER.test(commentText);
};

export const getFeedbackSectionTitle = (interviewKind: InterviewKind, sourceStepName: string): string =>
  interviewKind === INTERVIEW_KINDS.HR
    ? `${FEEDBACK_TITLES[interviewKind]} from ${sourceStepName} step`
    : FEEDBACK_TITLES[interviewKind];

export const orderGroupFeedbacks = (feedbacks: FeedbackData[], userUuid?: Uuid): FeedbackData[] => {
  const empty = feedbacks.find(feedback => !feedback.comment && feedback.approver.uuid === userUuid);
  const otherFeedbacks = feedbacks.filter(feedback => !!feedback.comment);
  return empty ? [empty, ...otherFeedbacks] : otherFeedbacks;
};

export const getPostFeedbackBody = (feedback: Partial<FeedbackData>) =>
  Object.keys(feedback).length
    ? { ...feedback, is_draft: false }
    : {
        is_draft: true,
        comment: '',
        positive: null,
        english_level: null,
        level: null,
      };

export const getFeedbackFormInitialValues = (feedback: FeedbackData, isEdit: boolean): FeedbackFormValues => ({
  is_draft: false,
  comment_initial: feedback.comment,
  comment: feedback.comment,
  positive: feedback.positive,
  english_level: feedback.english_level ? { value: feedback.english_level, label: feedback.english_level } : undefined,
  level: feedback.level ? { value: feedback.level, label: feedback.level } : undefined,
  is_edit: isEdit,
});

export const getFeedbackGroupView = (
  group: InterviewFeedbackGroup,
  options: {
    currentUserUuid?: Uuid;
    readOnly: boolean;
    currentStepId?: string;
    stepId: Uuid;
    groupsCount: number;
    showAllFeedbacks: boolean;
  }
) => {
  const visibleFeedbacks = orderGroupFeedbacks(group.feedbacks as FeedbackData[], options.currentUserUuid);
  const hasApiCounters = group.feedbacksPosted != null && group.feedbacksTotal != null;
  const listPostedCount = group.feedbacks.filter(isFeedbackPosted).length;
  const postedCount = hasApiCounters ? group.feedbacksPosted! : listPostedCount;
  const totalCount = hasApiCounters ? group.feedbacksTotal! : Math.max(group.feedbacks.length, listPostedCount);
  const canEdit = !options.readOnly && options.currentStepId === options.stepId;
  const isEditableGroup = canEdit && (group.isCurrent || options.groupsCount === 1);
  const showAll = options.groupsCount > 1 || options.showAllFeedbacks;
  const incomplete = totalCount === 0 ? postedCount === 0 : postedCount < totalCount;
  const hasForm =
    isEditableGroup &&
    visibleFeedbacks.some(
      feedback => !!feedback.is_editable && !feedback.comment && feedback.approver.uuid === options.currentUserUuid
    );

  return {
    visibleFeedbacks,
    postedFeedbacks: visibleFeedbacks.filter(feedback => !!feedback.comment),
    postedCount,
    totalCount,
    isEditableGroup,
    showAll,
    incomplete,
    hasForm,
  };
};

export const isFeedbackCardEditable = (feedback: FeedbackData, isEditableGroup: boolean): boolean =>
  isEditableGroup && !!feedback.is_editable;
